import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Download, Eye, X, Send, Loader2, Users, CheckSquare, Square, ArrowLeft } from "lucide-react";
import { jsPDF } from "jspdf";
import { toast } from "sonner";
import { publishTest, teacherStudents } from "@/lib/api";

/**
 * TestPDF — Preview, download and publish a practice test.
 * For class tests: shows a student-selection dialog before publish.
 * For individual student tests (testData.learner_id set): publishes directly.
 */

/**
 * Resolves the answer key letter (A/B/C/D) for a question.
 *
 * Handles two storage formats:
 *   1. correct_answer is already a letter ("A" / "B" / "C" / "D")
 *      — used by fuel_questions (NCERT dataset)
 *   2. correct_answer is the full option text
 *      — used by Claude-generated questions (refresh / pool generation)
 */
function resolveAnswerLetter(q) {
  const ca = (q.correct_answer || "").trim();
  // Case 1: already a single letter A-D
  if (/^[A-D]$/i.test(ca)) return ca.toUpperCase();
  // Case 2: full text — find by exact match in options list
  const idx = (q.options || []).findIndex((opt) => opt === ca);
  if (idx >= 0) return String.fromCharCode(65 + idx);
  // Case 3: case-insensitive / trimmed fallback
  const idxFuzzy = (q.options || []).findIndex(
    (opt) => (opt || "").trim().toLowerCase() === ca.toLowerCase()
  );
  return idxFuzzy >= 0 ? String.fromCharCode(65 + idxFuzzy) : "?";
}

export default function TestPDF({ testData, onClose, teacherId, grade, onBack }) {
  const [showPreview, setShowPreview] = useState(true);
  const [publishLoading, setPublishLoading] = useState(false);
  const [publishStatus, setPublishStatus] = useState(testData.status || "draft");
  const [showStudentSelect, setShowStudentSelect] = useState(false);
  const [students, setStudents] = useState([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const previewRef = useRef(null);

  const isMultiTopic = testData.topics && testData.topics.length > 1;

  // ── PDF helpers ────────────────────────────────────────────────────────────
  const generateStudentPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - 2 * margin;
    let yPos = margin;

    const checkPageBreak = (requiredHeight) => {
      if (yPos + requiredHeight > pageHeight - margin) {
        doc.addPage();
        yPos = margin;
        return true;
      }
      return false;
    };

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Practice Test", margin, yPos);
    yPos += 8;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Grade: ${testData.grade}`, margin, yPos); yPos += 5;
    doc.text(`Subject: ${testData.subject}`, margin, yPos); yPos += 5;
    if (isMultiTopic) {
      doc.text(`Chapters: ${testData.topics.join(", ")}`, margin, yPos);
    } else {
      doc.text(`Chapter: ${testData.chapter}`, margin, yPos);
    }
    yPos += 5;
    if (!isMultiTopic) {
      const stText = `Sub-topics: ${(testData.sub_topics || []).join(", ")}`;
      const stLines = doc.splitTextToSize(stText, contentWidth);
      doc.text(stLines, margin, yPos); yPos += stLines.length * 5;
    }
    doc.text(`Date: ${new Date().toLocaleDateString()}`, margin, yPos); yPos += 5;
    if (testData.learner_name) { doc.text(`Student: ${testData.learner_name}`, margin, yPos); yPos += 5; }
    if (testData.add_marks && testData.marks_per_question) {
      const totalMarks = testData.questions.length * testData.marks_per_question;
      doc.text(`Total Marks: ${totalMarks}`, margin, yPos); yPos += 5;
    }
    yPos += 5;
    doc.setFontSize(9);
    doc.setFont("helvetica", "italic");
    doc.text("School: _________________________", margin, yPos); yPos += 8;
    doc.setFont("helvetica", "normal");
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos); yPos += 8;

    testData.questions.forEach((q, idx) => {
      const questionNum = idx + 1;
      let questionLines = doc.splitTextToSize(`${questionNum}. ${q.question_text}`, contentWidth - 10);
      const marksText = testData.add_marks && q.marks ? ` [${q.marks} mark${q.marks > 1 ? "s" : ""}]` : "";
      if (marksText) questionLines[0] = questionLines[0] + marksText;
      const questionHeight = questionLines.length * 6 + q.options.length * 7 + 15;
      checkPageBreak(questionHeight);
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text(questionLines, margin, yPos); yPos += questionLines.length * 6 + 3;
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      q.options.forEach((opt, optIdx) => {
        const letter = String.fromCharCode(65 + optIdx);
        const optLines = doc.splitTextToSize(`${letter}. ${opt}`, contentWidth - 15);
        checkPageBreak(optLines.length * 5 + 3);
        doc.text(optLines, margin + 8, yPos); yPos += optLines.length * 5 + 2;
      });
      yPos += 5;
    });

    const filename = testData.learner_name
      ? `Test_${testData.subject}_${testData.learner_name.replace(/\s+/g, "_")}.pdf`
      : `Test_${testData.subject}_Grade${testData.grade}.pdf`;
    doc.save(filename);
    return filename;
  };

  const generateAnswerKeyPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    let yPos = margin;
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Answer Key", margin, yPos); yPos += 8;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Grade: ${testData.grade}  |  Subject: ${testData.subject}`, margin, yPos); yPos += 5;
    if (isMultiTopic) {
      doc.text(`Chapters: ${testData.topics.join(", ")}`, margin, yPos);
    } else {
      doc.text(`Chapter: ${testData.chapter}`, margin, yPos);
    }
    yPos += 5;
    doc.text(`Date: ${new Date().toLocaleDateString()}`, margin, yPos); yPos += 8;
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos); yPos += 8;
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Teacher Answer Key", margin, yPos); yPos += 8;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Confidential — Do not distribute to students", margin, yPos); yPos += 10;
    const cols = 4, boxSize = 25, colWidth = (pageWidth - 2 * margin) / cols;
    testData.questions.forEach((q, idx) => {
      const questionNum = idx + 1;
      const col = idx % cols, row = Math.floor(idx / cols);
      const x = margin + col * colWidth;
      const y = yPos + row * (boxSize + 5);
      doc.setDrawColor(200, 200, 200);
      doc.setFillColor(240, 255, 245);
      doc.rect(x, y, colWidth - 5, boxSize, "FD");
      const correctLetter = resolveAnswerLetter(q);
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);
      doc.text(`Q${questionNum}`, x + 3, y + 8);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 150, 0);
      doc.text(correctLetter, x + colWidth / 2 - 7, y + 20);
    });
    const filename = testData.learner_name
      ? `AnswerKey_${testData.subject}_${testData.learner_name.replace(/\s+/g, "_")}.pdf`
      : `AnswerKey_${testData.subject}_Grade${testData.grade}.pdf`;
    doc.save(filename);
    return filename;
  };

  const handleDownloadBoth = () => {
    const studentFile = generateStudentPDF();
    setTimeout(() => {
      const answerKeyFile = generateAnswerKeyPDF();
      toast.success(`Downloaded: ${studentFile} and ${answerKeyFile}`);
    }, 500);
  };

  // ── Publish logic ──────────────────────────────────────────────────────────
  const doPublish = async (learnerIds) => {
    setPublishLoading(true);
    try {
      const result = await publishTest({
        test_id: testData.test_id,
        teacher_id: teacherId,
        grade,
        learner_ids: learnerIds,
      });
      setPublishStatus("published");
      setShowStudentSelect(false);
      toast.success(
        `Test published to ${result.published_count} student${result.published_count === 1 ? "" : "s"}!`
      );
    } catch (err) {
      toast.error("Failed to publish: " + (err.response?.data?.detail || err.message));
    } finally {
      setPublishLoading(false);
    }
  };

  const handlePublishClick = async () => {
    // Individual student test — publish directly (old behaviour)
    if (testData.learner_id) {
      await doPublish([testData.learner_id]);
      return;
    }
    // Class test — open student selection dialog
    if (students.length === 0) {
      setStudentsLoading(true);
      try {
        const data = await teacherStudents(teacherId, grade);
        const list = Array.isArray(data) ? data : data.students || [];
        setStudents(list);
        setSelectedStudentIds(list.map((s) => s.learner_id));
      } catch (e) {
        toast.error("Could not load students: " + e.message);
        setStudentsLoading(false);
        return;
      } finally {
        setStudentsLoading(false);
      }
    }
    setShowStudentSelect(true);
  };

  // ── Student select modal ────────────────────────────────────────────────────
  const StudentSelectModal = () => {
    const allSelected = selectedStudentIds.length === students.length && students.length > 0;

    const toggle = (id) => {
      setSelectedStudentIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      );
    };
    const toggleAll = () => {
      setSelectedStudentIds(allSelected ? [] : students.map((s) => s.learner_id));
    };

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" data-testid="student-select-modal">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" style={{ maxHeight: "90vh" }}>
          <div className="flex items-center justify-between p-5 border-b">
            <div>
              <h3 className="text-base font-bold text-gray-900">Select Students</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {selectedStudentIds.length} of {students.length} selected
              </p>
            </div>
            <button onClick={() => setShowStudentSelect(false)} className="p-1 rounded hover:bg-gray-100 text-gray-500">
              <X size={18} />
            </button>
          </div>

          <div className="overflow-y-auto" style={{ maxHeight: "calc(90vh - 170px)" }}>
            {/* Select all row */}
            <div
              className="flex items-center gap-3 px-5 py-3 border-b bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={toggleAll}
              data-testid="select-all-students"
            >
              {allSelected ? (
                <CheckSquare size={18} className="text-emerald-600 flex-shrink-0" />
              ) : (
                <Square size={18} className="text-gray-400 flex-shrink-0" />
              )}
              <span className="text-sm font-semibold text-gray-700">Entire Class</span>
              <span className="ml-auto text-xs text-gray-400">{students.length} students</span>
            </div>

            {studentsLoading ? (
              <div className="py-10 flex justify-center">
                <Loader2 size={20} className="animate-spin text-gray-400" />
              </div>
            ) : students.length === 0 ? (
              <div className="py-10 text-center text-sm text-gray-400">No students found for this grade.</div>
            ) : (
              students.map((s) => {
                const checked = selectedStudentIds.includes(s.learner_id);
                return (
                  <div
                    key={s.learner_id}
                    onClick={() => toggle(s.learner_id)}
                    className="flex items-center gap-3 px-5 py-3 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100"
                    data-testid={`student-select-${s.learner_id}`}
                  >
                    {checked ? (
                      <CheckSquare size={17} className="text-emerald-600 flex-shrink-0" />
                    ) : (
                      <Square size={17} className="text-gray-400 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{s.name || s.learner_id}</p>
                      {s.name && (
                        <p className="text-xs text-gray-400 truncate">{s.learner_id}</p>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="p-5 border-t bg-gray-50 flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowStudentSelect(false)}
              className="flex-1 text-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={() => doPublish(selectedStudentIds)}
              disabled={publishLoading || selectedStudentIds.length === 0}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-sm"
              data-testid="confirm-publish-btn"
            >
              {publishLoading ? (
                <><Loader2 size={14} className="mr-1 animate-spin" /> Publishing…</>
              ) : (
                <><Send size={14} className="mr-1" /> Publish to {selectedStudentIds.length}</>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4" data-testid="test-pdf-view">
      {showStudentSelect && <StudentSelectModal />}

      {/* Header row */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          {onBack && (
            <Button
              size="sm"
              variant="outline"
              onClick={onBack}
              className="text-xs"
              data-testid="preview-back-btn"
            >
              <ArrowLeft className="w-3.5 h-3.5 mr-1" />
              Back
            </Button>
          )}
          <div>
            <h3 className="font-teacher text-lg font-semibold">Test Ready</h3>
            <p className="text-xs text-slate-600 mt-1">
              Status:{" "}
              <span className={`font-semibold ${publishStatus === "published" ? "text-emerald-600" : "text-amber-600"}`}>
                {publishStatus === "published" ? "✓ Published" : "Draft"}
              </span>
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => setShowPreview(!showPreview)} className="text-xs">
            <Eye className="w-4 h-4 mr-1" />
            {showPreview ? "Hide" : "Show"} Preview
          </Button>
          {/* Publish / Student select */}
          {publishStatus !== "published" && (
            <Button
              size="sm"
              onClick={handlePublishClick}
              disabled={publishLoading || studentsLoading}
              className="bg-emerald-600 hover:bg-emerald-700 text-xs"
              data-testid="publish-test-btn"
            >
              {publishLoading || studentsLoading ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> {studentsLoading ? "Loading…" : "Publishing…"}</>
              ) : testData.learner_id ? (
                <><Send className="w-4 h-4 mr-1" /> Publish to Student</>
              ) : (
                <><Users className="w-4 h-4 mr-1" /> Select Students &amp; Publish</>
              )}
            </Button>
          )}
          {publishStatus === "published" && (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-semibold bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-lg">
              ✓ Published
            </span>
          )}
          <Button size="sm" onClick={handleDownloadBoth} className="bg-sky-600 hover:bg-sky-700 text-xs">
            <Download className="w-4 h-4 mr-1" />
            Download PDFs
          </Button>
          <Button size="sm" variant="outline" onClick={onClose} className="text-xs">
            <X className="w-4 h-4 mr-1" />
            Close
          </Button>
        </div>
      </div>

      {/* Preview */}
      {showPreview && (
        <Card className="p-6 bg-white border border-slate-300 max-h-[600px] overflow-y-auto">
          <div ref={previewRef} className="space-y-6">
            <div className="border-b pb-4">
              <h2 className="font-teacher text-xl font-bold mb-3">Practice Test</h2>
              <div className="text-sm space-y-1 text-slate-700">
                <p><strong>Grade:</strong> {testData.grade}</p>
                <p><strong>Subject:</strong> {testData.subject}</p>
                {isMultiTopic ? (
                  <p><strong>Chapters:</strong> {testData.topics.join(", ")}</p>
                ) : (
                  <>
                    <p><strong>Chapter:</strong> {testData.chapter}</p>
                    <p><strong>Sub-topics:</strong> {(testData.sub_topics || []).join(", ")}</p>
                  </>
                )}
                <p><strong>Date:</strong> {new Date().toLocaleDateString()}</p>
                {testData.learner_name && <p><strong>Student:</strong> {testData.learner_name}</p>}
                {testData.add_marks && testData.marks_per_question && (
                  <p><strong>Total Marks:</strong> {testData.questions.length * testData.marks_per_question}</p>
                )}
                <p className="italic text-slate-500">School: _________________________</p>
              </div>
            </div>

            <div className="space-y-6">
              {testData.questions.map((q, idx) => (
                <div key={idx} className="space-y-2">
                  <p className="font-semibold text-slate-900">
                    {idx + 1}.{" "}
                    {testData.add_marks && q.marks && (
                      <span className="text-xs text-slate-600 mr-1">[{q.marks} mark{q.marks > 1 ? "s" : ""}]</span>
                    )}
                    {q.question_text}
                  </p>
                  <div className="pl-4 space-y-1.5">
                    {q.options.map((opt, optIdx) => (
                      <p key={optIdx} className="text-sm text-slate-700">
                        <span className="font-semibold mr-2">{String.fromCharCode(65 + optIdx)}.</span>
                        {opt}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t pt-6 mt-8">
              <h3 className="font-teacher text-lg font-bold mb-3 text-slate-800">Answer Key (Teacher Only)</h3>
              <p className="text-xs text-slate-600 mb-4">
                This section appears on a separate page in the PDF.
              </p>
              <div className="grid grid-cols-5 gap-2">
                {testData.questions.map((q, idx) => {
                  const correctLetter = resolveAnswerLetter(q);
                  return (
                    <div key={idx} className="text-sm bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                      <span className="font-semibold">{idx + 1}.</span> {correctLetter}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
